import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  EmptyState,
  Input,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type Density,
  type Tone,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type {
  CompaniesListResponse,
  CompanyApprovalStatus,
  CompanyDTO,
} from "@wmart/shared-types";

const STATUS_TONE: Record<CompanyApprovalStatus, Tone> = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
};

export function CompaniesList() {
  const navigate = useNavigate();
  const { notify, notifyProblem } = useToast();
  const [items, setItems] = useState<CompanyDTO[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [density, setDensity] = useState<Density>("default");

  // Density: read from localStorage on mount (Q5 carry-forward).
  useEffect(() => {
    const stored = window.localStorage.getItem("wmart.density");
    if (stored === "compact" || stored === "default" || stored === "cosy") {
      setDensity(stored);
    }
  }, []);

  // Debounce the free-text query — mirrors ProductsList / CustomersList.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  async function fetchPage(reset: boolean, cursor: string | null) {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (debouncedQ.trim()) params.set("q", debouncedQ.trim());
      if (cursor) params.set("cursor", cursor);
      const data = await api.get<CompaniesListResponse>(
        `/v1/admin/companies?${params.toString()}`,
      );
      setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
      setNextCursor(data.nextCursor);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load companies");
    } finally {
      setLoading(false);
    }
  }

  // Re-fetch on search change.
  useEffect(() => {
    void fetchPage(true, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  const hasAnyFilter = debouncedQ.trim() !== "";

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-display-sm text-ink-900">Companies</h1>
        <Button onClick={() => navigate("/companies/new")}>New company</Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-72">
          <Input
            type="search"
            label="Search"
            placeholder="Search by name or slug…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {hasAnyFilter ? (
          <Button variant="link" size="sm" onClick={() => setQ("")}>
            Clear
          </Button>
        ) : null}
      </div>

      {items.length === 0 && !loading ? (
        <EmptyState
          icon="package"
          title="No companies match"
          body="Adjust filters or create a new company to get started."
        />
      ) : (
        <>
          <Table density={density}>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Slug</TH>
                <TH>VAT</TH>
                <TH>Tier</TH>
                <TH>Status</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((c) => (
                <TR key={c.id}>
                  <TD>
                    <Link
                      to={`/companies/${c.id}`}
                      className="text-ink-900 hover:underline"
                    >
                      {c.name}
                    </Link>
                  </TD>
                  <TD className="font-mono text-body-sm text-ink-500">
                    {c.slug}
                  </TD>
                  <TD className="text-body-sm text-ink-700">
                    {c.vat_number
                      ? `${c.vat_country} ${c.vat_number}`
                      : c.vat_country}
                  </TD>
                  <TD className="text-body-sm">{c.default_tier}</TD>
                  <TD>
                    <StatusPill tone={STATUS_TONE[c.approval_status]}>
                      {c.approval_status}
                    </StatusPill>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>

          {nextCursor ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                onClick={() => void fetchPage(false, nextCursor)}
                disabled={loading}
                loading={loading}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
